import { isSuccessStatusCode, ServiceError } from "@gamedev-web/gamedev-sdk-core";
import { getAccessToken, getCrmApiUrl, logServerError } from "../config";
import { EntityName, ExternalEntityName } from "../metadata";
import { OneGamingCrmErrorType } from "../models";
import { getCachedFunction } from "./getCachedFunction";

export const getLogicalCollectionName = async (entityName: EntityName): Promise<string> => {
  try {
    const logicalCollectionNames = await getEntityCollectionNames();
    if (!logicalCollectionNames[entityName]) {
      const errorMessage = `Failed to find the logical collection name for entity ${entityName}`;
      logServerError(errorMessage, OneGamingCrmErrorType.FailedToFindLogicalCollectionName);
      throw new Error(errorMessage);
    }
    return logicalCollectionNames[entityName];
  } catch (error) {
    const err = error as Error;
    logServerError(err.message, OneGamingCrmErrorType.FailedToFindLogicalCollectionName, err);
    throw new ServiceError(
      `Failed to get logical collection name for ${entityName}: ${err.message}`,
      500
    );
  }
};

const fetchEntityCollectionNames = async () => {
  try {
    const crmToken = await getAccessToken();
    const url = await getCrmApiUrl();
    const filterQuery = [...Object.values(EntityName), ...Object.values(ExternalEntityName)]
      .map(entityName => `LogicalName eq '${entityName}'`)
      .join(" or ");

    const requestUri = `${url}/EntityDefinitions?$select=LogicalName,LogicalCollectionName&$filter=${filterQuery}`;
    const requestOptions: RequestInit = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${crmToken.token_type} ${crmToken.access_token}`,
      },
    };

    const response = await fetch(requestUri, requestOptions);
    const contentData = await response.json();

    if (!isSuccessStatusCode(response.status) || !contentData) {
      throw new ServiceError(
        "Failed to fetch EntityDefinitions to get logical collection names",
        response.status
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entityDefinitions: any[] = contentData.value || [];
    const collectionNamesMap: Record<string, string> = entityDefinitions.reduce(
      (map, entityDefinition) => {
        map[entityDefinition.LogicalName] = entityDefinition.LogicalCollectionName;
        return map;
      },
      {}
    );
    return collectionNamesMap;
  } catch (error) {
    const err = error as Error;
    logServerError(err.message, OneGamingCrmErrorType.FailedToFindLogicalCollectionName, err);
    throw error;
  }
};

export const getEntityCollectionNames = getCachedFunction({
  id: "getEntityCollectionNames",
  inputFunction: fetchEntityCollectionNames,
  warmUpOnServiceStart: true,
});
